import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Twitter, Linkedin, Github, Mail, ArrowRight, CheckCircle2, Loader2, Terminal } from "lucide-react";
import api, { fetchCsrfToken } from "../lib/api";
import { getBootcampStatus } from "../lib/bootcamp";

const LogoIcon = () => (
  <div className="w-7 h-7 bg-primary flex items-center justify-center flex-shrink-0">
    <Terminal className="w-4 h-4 text-background" />
  </div>
);

const Footer = () => {

  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | success | already | error
  const [errorMsg, setErrorMsg] = useState("");
  const [bootcampStatus, setBootcampStatus] = useState(null);

  useEffect(() => {
    getBootcampStatus().then(data => setBootcampStatus(data));
  }, []);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setStatus("error");
      setErrorMsg("Please enter your email.");
      return;
    }

    setStatus("loading");
    setErrorMsg("");

    try {
      await fetchCsrfToken();
      const res = await api.post("/subscribe", { email: email.trim().toLowerCase() });
      const msg = res.data?.message || "";
      if (msg.toLowerCase().includes("already")) {
        setStatus("already");
      } else {
        setStatus("success");
      }
    } catch (err) {
      setStatus("error");
      const detail = err.response?.data?.detail;
      if (typeof detail === "string" && detail.toLowerCase().includes("rate")) {
        setErrorMsg("Too many attempts. Try again later.");
      } else if (err.response?.status === 422) {
        setErrorMsg("Invalid email address.");
      } else {
        setErrorMsg("Something went wrong. Try again.");
      }
    }
  };

  return (
    <footer className="bg-background border-t border-border pt-20 pb-10 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-16">
          
          {/* Column 1: Brand */}
          <div className="md:col-span-4 flex flex-col items-center md:items-start gap-6 text-center md:text-left">
            <Link to="/" className="flex items-center gap-2.5 group w-fit">
              <LogoIcon />
              <span className="font-bold text-[18px] text-white tracking-tight group-hover:text-primary transition-colors">
                DevHire<span className="text-primary">Lab</span>
              </span>
            </Link>
            <p className="font-sora text-muted-foreground text-sm leading-relaxed max-w-xs">
              Learn Python. Build real skills. Get hired. The most practical path to mastering modern programming.
            </p>
            <div className="flex gap-4">
              {[Twitter, Linkedin, Github].map((Icon, i) => (
                <a
                  key={i}
                  href="#"
                  className="w-10 h-10 border border-border rounded-lg flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary transition-all"
                >
                  <Icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Column 2: Links */}
          <div className="md:col-span-4 grid grid-cols-1 sm:grid-cols-2 gap-8 text-center md:text-left">
            <div className="flex flex-col gap-6">
              <h4 className="text-[10px] font-bold text-white uppercase tracking-[0.2em]">Platform</h4>
              <nav className="flex flex-col gap-4">
                {[
                  { label: "Tutorials", path: "/tutorials" },
                  { label: "Bootcamp", path: "/bootcamp" },
                  { label: "Simulator", path: "/playground" },
                  { label: "AI Interview", path: "/ai-interview" }
                ].map((link) => (
                  <Link key={link.label} to={link.path} className="font-sora text-muted-foreground text-sm hover:text-primary transition-colors flex items-center gap-2">
                    {link.label}
                    {link.label === 'Bootcamp' && bootcampStatus && (
                      <div 
                        className="w-1 h-1 rounded-full animate-pulse" 
                        style={{ 
                          backgroundColor: bootcampStatus.status === 'enrolling' ? '#00E676' : 
                                          bootcampStatus.status === 'in_progress' ? '#3B82F6' :
                                          bootcampStatus.status === 'completed' ? '#6B7280' : '#F59E0B'
                        }} 
                      />
                    )}
                  </Link>
                ))}
              </nav>
            </div>
            <div className="flex flex-col gap-6">
              <h4 className="text-[10px] font-bold text-white uppercase tracking-[0.2em]">Company</h4>
              <nav className="flex flex-col gap-4">
                {[
                  { label: "About", path: "/about" },
                  { label: "Blog", path: "#" },
                  { label: "Contact", path: "/contact" },
                  { label: "Privacy", path: "/privacy" }
                ].map((link) => (
                  <Link key={link.label} to={link.path} className="font-sora text-muted-foreground text-sm hover:text-primary transition-colors">
                    {link.label}
                  </Link>
                ))}
              </nav>
            </div>
          </div>

          {/* Column 3: Subscribe */}
          <div className="md:col-span-4 flex flex-col items-center md:items-start gap-6">
            <h4 className="text-[10px] font-bold text-white uppercase tracking-[0.2em] text-center md:text-left">Stay Updated</h4>
            <p className="font-sora text-muted-foreground text-sm leading-relaxed text-center md:text-left max-w-sm">
              Get notified on new course launches and platform updates.
            </p>

            {status === "success" || status === "already" ? (
              <div className="bg-primary-subtle border-primary-subtle flex items-center gap-3 w-full h-14 px-5 border">
                <CheckCircle2 className="w-5 h-5 text-primary shrink-0" />
                <span className="text-primary text-[11px] font-semibold uppercase tracking-widest">
                  {status === "already" ? "Already subscribed" : "You're on the list!"}
                </span>
              </div>
            ) : (
              <>
                <form className="flex flex-col sm:flex-row gap-0 w-full" onSubmit={handleSubscribe}>
                  <input
                    type="email"
                    placeholder="ENTER EMAIL"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); if (status === "error") setStatus("idle"); }}
                    disabled={status === "loading"}
                    className={`bg-card rounded-none flex-1 h-14 px-5 text-white text-[10px] focus:outline-none focus:border-primary transition-all disabled:opacity-50 border ${status === 'error' ? 'border-accent' : 'border-[#1F2937]'}`}
                  />
                  <button
                    type="submit"
                    disabled={status === "loading"}
                    className="h-14 px-6 bg-primary hover:bg-primary/90 text-background font-semibold text-[10px] flex items-center justify-center gap-2 transition-all disabled:opacity-70"
                  >
                    {status === "loading" ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        SUBSCRIBE
                        <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </form>
                {status === "error" && errorMsg && (
                  <p className="text-accent text-[10px] font-semibold uppercase tracking-widest mt-[-12px]">
                    {errorMsg}
                  </p>
                )}
              </>
            )}
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-10 border-t border-border flex flex-col md:flex-row justify-between items-center gap-8 text-center">
          <p className="text-muted-foreground text-[10px] uppercase tracking-widest">
            © 2026 DevHireLab. All rights reserved.
          </p>
          <div className="flex flex-wrap justify-center gap-x-8 gap-y-4">
            <Link to="/privacy" className="text-muted-foreground text-[10px] uppercase tracking-widest hover:text-white transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms" className="text-muted-foreground text-[10px] uppercase tracking-widest hover:text-white transition-colors">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
